/**
 * Favorite Button Component
 *
 * A star icon button for adding/removing technologies from favorites.
 */

'use client';

import { useAuth } from '@/lib/auth';
import { useFavorites } from '@/lib/hooks/useUserData';

interface FavoriteButtonProps {
  technologyId: string;
  technologyName?: string;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
  onUnauthorized?: () => void;
}

export default function FavoriteButton({
  technologyId,
  technologyName,
  size = 'md',
  showLabel = false,
  className = '',
  onUnauthorized,
}: FavoriteButtonProps) {
  const { isAuthenticated, login } = useAuth();
  const { isFavorite, addFavorite, removeFavorite } = useFavorites();
  const favorited = isFavorite(technologyId);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Check if user is authenticated
    if (!isAuthenticated) {
      if (onUnauthorized) {
        onUnauthorized();
      } else {
        // Default behavior: redirect to login with return to dashboard
        login({ returnTo: '/tech-stack-dashboard/favorites' });
      }
      return;
    }

    if (favorited) {
      removeFavorite(technologyId);
    } else {
      addFavorite(technologyId);
    }
  };

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const buttonSizeClasses = {
    sm: 'p-1',
    md: 'p-1.5',
    lg: 'p-2',
  };

  const getTitle = () => {
    if (!isAuthenticated) {
      return 'Login to save favorites';
    }
    return favorited ? 'Remove from favorites' : 'Add to favorites';
  };

  return (
    <button
      onClick={handleClick}
      className={`${buttonSizeClasses[size]} rounded-lg transition-all hover:bg-cream/10 ${className}`}
      title={getTitle()}
      aria-label={getTitle()}
    >
      <div className="flex items-center gap-2">
        <svg
          className={`${sizeClasses[size]} transition-all ${
            favorited ? 'fill-accent text-accent' : 'fill-none text-foreground-tertiary'
          }`}
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
          />
        </svg>
        {showLabel && (
          <span className="text-sm text-foreground/90">
            {favorited ? 'Favorited' : 'Favorite'}
          </span>
        )}
      </div>
    </button>
  );
}
